import { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  LayersControl,
  TileLayer,
  FeatureGroup,
  GeoJSON,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Select from "react-select";
import { selector } from "../../api";
import { boundaryCRUD } from "../../actions/boundary";
import { buildingCRUD } from "../../actions/building";
import { settlementCRUD } from "../../actions/settlement";
import { number } from "../../utils";
import { Footer } from "../../layout/footer";
import { Loading } from "../constant";
import { TILE_LAYERS, BUILDING_COLORS, CATEGORY_MAP } from "../map/mapConstants";
import { buildBuildingPopup } from "../map/popupHelpers";

export const SettlementOverview = () => {
  const { BaseLayer } = LayersControl;
  const mapRef = useRef();
  const user = selector.User();

  // Data states
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [boundaryGeoJson, setBoundaryGeoJson] = useState(null);
  const [buildingGeoJson, setBuildingGeoJson] = useState(null);
  const [zoom, setZoom] = useState(0);
  const [error, setError] = useState(null);

  // Cascading selector states (admin only)
  const [islands, setIslands] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [zones, setZones] = useState([]);

  const [selectedIsland, setSelectedIsland] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedLocality, setSelectedLocality] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);

  const isAdmin = user?.is_staff;

  // Load islands for admin selector
  useEffect(() => {
    if (isAdmin) {
      boundaryCRUD.loadAllIslands().then((resp) => {
        setIslands(resp.data);
      }).catch((error) => console.log(error));
    }
  }, [isAdmin]);

  // Auto-load data for non-admin users (wait for user to be available)
  useEffect(() => {
    if (user && !isAdmin) {
      loadReport(null);
    }
  }, [user]);

  // Cascading: load regions when island selected
  useEffect(() => {
    setSelectedRegion(null);
    setRegions([]);
    setDistricts([]);
    setLocalities([]);
    setZones([]);
    if (selectedIsland) {
      boundaryCRUD.loadBoundaryByIsland(1000, 0, "Region", selectedIsland).then((resp) => {
        setRegions(resp.data.results);
      }).catch((error) => console.log(error));
    }
  }, [selectedIsland]);

  // Cascading: load districts when region selected
  useEffect(() => {
    setSelectedDistrict(null);
    setDistricts([]);
    setLocalities([]);
    setZones([]);
    if (selectedRegion) {
      boundaryCRUD.loadBoundaryByParent(selectedRegion).then((resp) => {
        setDistricts(resp.data);
      }).catch((error) => console.log(error));
    }
  }, [selectedRegion]);

  // Cascading: load localities when district selected
  useEffect(() => {
    setSelectedLocality(null);
    setLocalities([]);
    setZones([]);
    if (selectedDistrict) {
      boundaryCRUD.loadBoundaryByParent(selectedDistrict).then((resp) => {
        setLocalities(resp.data);
      }).catch((error) => console.log(error));
    }
  }, [selectedDistrict]);

  // Cascading: load zones when locality selected
  useEffect(() => {
    setSelectedZone(null);
    setZones([]);
    if (selectedLocality) {
      boundaryCRUD.loadBoundaryByParent(selectedLocality).then((resp) => {
        setZones(resp.data);
      }).catch((error) => console.log(error));
    }
  }, [selectedLocality]);

  // Get the most specific selected boundary ID
  const getSelectedBoundaryId = () => {
    if (selectedZone) return selectedZone;
    if (selectedLocality) return selectedLocality;
    if (selectedDistrict) return selectedDistrict;
    if (selectedRegion) return selectedRegion;
    return null;
  };

  // Determine zoom level based on boundary type
  const getZoomForBoundaryType = (typeId) => {
    // 21=Region, 22=District, 23=Locality/Shehia, 24=Zone
    switch (typeId) {
      case 21: return 10;
      case 22: return 11;
      case 23: return 13;
      case 24: return 15;
      default: return 10;
    }
  };

  // Load boundary polygon from our own API and fly map to it
  const loadBoundaryMap = (boundaryId, fallbackCentroid, fallbackTypeId) => {
    if (!boundaryId) {
      setBoundaryGeoJson(null);
      return;
    }
    boundaryCRUD.loadOneBoundaryMap(boundaryId).then((resp) => {
      // Response is an array like [{id, boundary_code, boundary_name, boundary_polygon, boundary_centroid}]
      const boundaryData = Array.isArray(resp.data) ? resp.data[0] : resp.data;
      if (!boundaryData) {
        setBoundaryGeoJson(null);
        return;
      }

      // If polygon exists, render it on the map
      if (boundaryData.boundary_polygon) {
        try {
          // boundary_polygon is already a GeoJSON geometry object from DRF
          const geometry = boundaryData.boundary_polygon;
          const geojson = {
            type: "FeatureCollection",
            features: [{
              type: "Feature",
              properties: { id: boundaryData.id, name: boundaryData.boundary_name },
              geometry: geometry,
            }]
          };
          setBoundaryGeoJson(geojson);
          if (mapRef.current) {
            const layer = L.geoJSON(geojson);
            const bounds = layer.getBounds();
            if (bounds.isValid()) {
              mapRef.current.flyToBounds(bounds, { padding: [30, 30] });
              return; // Already flew to bounds, skip centroid fly
            }
          }
        } catch (e) {
          console.log("Error rendering boundary polygon:", e);
          setBoundaryGeoJson(null);
        }
      }

      // No polygon or polygon failed — fly to centroid from map API response
      if (boundaryData.boundary_centroid?.coordinates && mapRef.current) {
        const coords = boundaryData.boundary_centroid.coordinates;
        const zoom = getZoomForBoundaryType(fallbackTypeId);
        mapRef.current.flyTo([coords[1], coords[0]], zoom);
      } else if (fallbackCentroid && mapRef.current) {
        // Use centroid from the settlement overview API as last resort
        const zoom = getZoomForBoundaryType(fallbackTypeId);
        mapRef.current.flyTo([fallbackCentroid.lat, fallbackCentroid.lng], zoom);
      }
    }).catch((error) => {
      console.log(error);
      setBoundaryGeoJson(null);
      // Still try to fly to fallback centroid on error
      if (fallbackCentroid && mapRef.current) {
        const zoom = getZoomForBoundaryType(fallbackTypeId);
        mapRef.current.flyTo([fallbackCentroid.lat, fallbackCentroid.lng], zoom);
      }
    });
  };

  // Zoom tracking
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.on("zoomend", function () {
        setZoom(mapRef.current._zoom);
      });
    }
  }, [mapRef.current]);

  const loadReport = (boundaryId) => {
    setLoading(true);
    setData(null);
    setError(null);
    setBoundaryGeoJson(null);
    setBuildingGeoJson(null);
    settlementCRUD
      .loadSettlementOverview(boundaryId)
      .then((resp) => {
        setData(resp.data);
        const centroid = resp.data.boundary?.centroid || null;
        const typeId = resp.data.boundary?.type_id || null;
        loadBoundaryMap(boundaryId, centroid, typeId);
      })
      .catch((err) => {
        console.log(err);
        setError(err.response?.data?.detail || err.message || "Failed to load settlement data");
      })
      .finally(() => {
        setLoading(false);
      });

    // Load buildings for the selected boundary
    if (boundaryId) {
      buildingCRUD.loadSpecificBuildingJson(boundaryId).then((resp) => {
        setBuildingGeoJson(resp.data);
      }).catch(() => setBuildingGeoJson(null));
    } else {
      buildingCRUD.loadBuildingJson().then((resp) => {
        setBuildingGeoJson(resp.data);
      }).catch(() => setBuildingGeoJson(null));
    }
  };

  const handleLoadReport = () => {
    const boundaryId = getSelectedBoundaryId();
    loadReport(boundaryId);
  };

  // Get boundary name for the header
  const getBoundaryName = () => {
    if (data?.boundary?.name) return data.boundary.name;
    return "All Boundaries";
  };

  const boundaryPolygonStyle = {
    color: "#6c63ff",
    weight: 3,
    fillColor: "#6c63ff",
    fillOpacity: 0.1,
  };

  // Boundary popup with settlement data
  const onBoundaryFeature = (feature, layer) => {
    const name = feature.properties.name || 'N/A';
    let html = '<div style="width:260px; font-size:13px;">' +
      '<div style="font-size:15px; font-weight:bold; margin-bottom:6px; color:#405189;">' + name + '</div>';

    if (data) {
      const catRow = (color, label, count) =>
        '<div style="display:flex; justify-content:space-between; margin-bottom:2px;">' +
          '<span><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:' + color + ';margin-right:4px;"></span>' + label + '</span>' +
          '<span style="font-weight:600;">' + (count || 0).toLocaleString() + '</span>' +
        '</div>';

      if (data.population) {
        html += '<hr style="margin:6px 0;"/>' +
          '<div style="display:flex; justify-content:space-between; margin-bottom:3px;">' +
            '<span style="font-weight:600;">Population:</span>' +
            '<span style="color:#405189; font-weight:bold;">' + (data.population.total || 0).toLocaleString() + '</span>' +
          '</div>' +
          '<div style="display:flex; justify-content:space-between; margin-bottom:3px;">' +
            '<span style="font-weight:600;">Households:</span>' +
            '<span style="color:#405189; font-weight:bold;">' + (data.population.households || 0).toLocaleString() + '</span>' +
          '</div>';
      }

      if (data.buildings) {
        html += '<hr style="margin:6px 0;"/>' +
          '<div style="font-weight:600; margin-bottom:4px;">Buildings (' + (data.buildings.total || 0).toLocaleString() + ' estimated)</div>';
        if (data.buildings.breakdown) {
          const colorMap = { 'Residential': '#2196F3', 'Commercial': '#FF9800', 'Civic': '#9C27B0', 'Religious': '#4CAF50', 'Mixed': '#F44336' };
          data.buildings.breakdown.forEach((item) => {
            html += catRow(colorMap[item.category_name] || '#666', item.category_name, item.count);
          });
        }
        html += '<div style="display:flex; justify-content:space-between; margin-top:4px; padding-top:4px; border-top:1px solid #eee;">' +
          '<span style="font-weight:600;">Registered:</span>' +
          '<span style="color:green; font-weight:bold;">' + (data.buildings.registered || 0).toLocaleString() + '</span>' +
        '</div>';
      }

      if (data.infrastructure) {
        html += '<hr style="margin:6px 0;"/>' +
          '<div style="font-weight:600; margin-bottom:3px;">Infrastructure Access</div>' +
          '<div style="display:flex; justify-content:space-between; margin-bottom:2px;">' +
            '<span>Electricity:</span><span style="font-weight:600;">' + (data.infrastructure.electricity_pct || 0) + '%</span>' +
          '</div>' +
          '<div style="display:flex; justify-content:space-between; margin-bottom:2px;">' +
            '<span>Water:</span><span style="font-weight:600;">' + (data.infrastructure.water_pct || 0) + '%</span>' +
          '</div>' +
          '<div style="display:flex; justify-content:space-between; margin-bottom:2px;">' +
            '<span>Sewer:</span><span style="font-weight:600;">' + (data.infrastructure.sewer_pct || 0) + '%</span>' +
          '</div>';
      }
    }
    html += '</div>';

    layer.bindPopup(html, { maxWidth: 320, minWidth: 260 });
    layer.on({
      mouseover: () => layer.setStyle({ weight: 4, color: '#ed071a', fillColor: '#6c63ff', fillOpacity: 0.15 }),
      mouseout: () => layer.setStyle(boundaryPolygonStyle),
    });
  };

  // Building feature handler with popup and color-coding
  const onBuildingFeature = (feature, layer) => {
    layer.bindPopup(buildBuildingPopup(feature.properties));
    layer.on({
      mouseover: () => layer.setStyle({ weight: 0.9, color: '#ed071a', fillColor: 'transparent', fillOpacity: 0.2 }),
      mouseout: () => {
        const color = BUILDING_COLORS[feature.properties.dictionary_building_category_id] || '#dddddd';
        layer.setStyle({ color, weight: zoom >= 15 ? 1 : 2, fillColor: color, fillOpacity: 0.3 });
      },
    });
  };

  const buildingStyle = (feature) => {
    const color = BUILDING_COLORS[feature.properties.dictionary_building_category_id] || '#dddddd';
    return { color, weight: zoom >= 15 ? 1 : 2, fillColor: color, fillOpacity: 0.3 };
  };

  return (
    <>
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Page title */}
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0 d-flex align-items-center">
                    <img
                      src="assets/images/logo/smz.png"
                      alt="SMZ"
                      height="40"
                      className="me-2"
                    />
                    Settlement Overview
                  </h4>
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item">
                        <a href="#">Reports</a>
                      </li>
                      <li className="breadcrumb-item active">Settlement Overview</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin: Cascading boundary selector */}
            {isAdmin && (
              <div className="row mb-3">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="row g-2 align-items-end">
                        <div className="col-md-2">
                          <label className="form-label fw-semibold">Island</label>
                          <Select
                            options={islands?.map((dt) => ({
                              value: dt.id,
                              label: dt.name,
                            }))}
                            value={
                              selectedIsland
                                ? islands
                                    .filter((i) => i.id === selectedIsland)
                                    .map((i) => ({ value: i.id, label: i.name }))[0]
                                : null
                            }
                            placeholder="Select island"
                            onChange={(e) => setSelectedIsland(e?.value || null)}
                            isClearable
                            isSearchable
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label fw-semibold">Region</label>
                          <Select
                            options={regions?.map((dt) => ({
                              value: dt.id,
                              label: dt.boundary_name,
                            }))}
                            value={
                              selectedRegion
                                ? regions
                                    .filter((r) => r.id === selectedRegion)
                                    .map((r) => ({ value: r.id, label: r.boundary_name }))[0]
                                : null
                            }
                            placeholder="Select region"
                            onChange={(e) => setSelectedRegion(e?.value || null)}
                            isClearable
                            isSearchable
                            isDisabled={!selectedIsland}
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label fw-semibold">District</label>
                          <Select
                            options={districts?.map((dt) => ({
                              value: dt.id,
                              label: dt.boundary_name,
                            }))}
                            value={
                              selectedDistrict
                                ? districts
                                    .filter((d) => d.id === selectedDistrict)
                                    .map((d) => ({ value: d.id, label: d.boundary_name }))[0]
                                : null
                            }
                            placeholder="Select district"
                            onChange={(e) => setSelectedDistrict(e?.value || null)}
                            isClearable
                            isSearchable
                            isDisabled={!selectedRegion}
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label fw-semibold">Shehia</label>
                          <Select
                            options={localities?.map((dt) => ({
                              value: dt.id,
                              label: dt.boundary_name,
                            }))}
                            value={
                              selectedLocality
                                ? localities
                                    .filter((l) => l.id === selectedLocality)
                                    .map((l) => ({ value: l.id, label: l.boundary_name }))[0]
                                : null
                            }
                            placeholder="Select shehia"
                            onChange={(e) => setSelectedLocality(e?.value || null)}
                            isClearable
                            isSearchable
                            isDisabled={!selectedDistrict}
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label fw-semibold">Zone</label>
                          <Select
                            options={zones?.map((dt) => ({
                              value: dt.id,
                              label: dt.boundary_name,
                            }))}
                            value={
                              selectedZone
                                ? zones
                                    .filter((z) => z.id === selectedZone)
                                    .map((z) => ({ value: z.id, label: z.boundary_name }))[0]
                                : null
                            }
                            placeholder="Select zone"
                            onChange={(e) => setSelectedZone(e?.value || null)}
                            isClearable
                            isSearchable
                            isDisabled={!selectedLocality}
                          />
                        </div>
                        <div className="col-md-2">
                          <button
                            className="btn btn-primary w-100"
                            onClick={handleLoadReport}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Loading...
                              </>
                            ) : (
                              <>
                                <i className="ri-search-line me-1"></i> Load Report
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Main content: Stats + Map */}
            {loading && !data && (
              <div className="row">
                <div className="col-12 text-center py-5">
                  <Loading />
                </div>
              </div>
            )}

            {error && (
              <div className="row">
                <div className="col-12">
                  <div className="alert alert-danger d-flex align-items-center" role="alert">
                    <i className="ri-error-warning-line me-2 fs-4"></i>
                    <div>{error}</div>
                  </div>
                </div>
              </div>
            )}

            {data && (
              <div className="row">
                {/* LEFT: Statistics */}
                <div className="col-xxl-5 col-lg-5">
                  {/* Boundary Name Header */}
                  <div className="card card-animate overflow-hidden">
                    <div className="card-body text-center text-white" style={{
                      background: 'linear-gradient(135deg, #405189, #0ab39c)',
                      padding: '16px',
                    }}>
                      <h4 className="mb-1 text-uppercase fw-bold">{getBoundaryName()}</h4>
                      {data.boundary?.code && (
                        <span className="badge bg-white bg-opacity-25 fs-6">
                          {data.boundary.type_name ? `${data.boundary.type_name} - ` : ''}{data.boundary.code}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Buildings Section */}
                  <div className="card card-animate">
                    <div className="card-header">
                      <h5 className="card-title mb-0">
                        <i className="ri-building-line me-2"></i>BUILDINGS
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="d-flex align-items-center mb-3">
                        <div>
                          <p className="text-muted mb-1">Total Estimated # Buildings</p>
                          <h3 className="mb-0 fw-bold">{number.fNumber(data.buildings?.total || 0)}</h3>
                        </div>
                      </div>
                      <hr className="border-danger border-2" />

                      <table className="table table-sm table-borderless mb-0">
                        <thead>
                          <tr>
                            <th className="text-muted" style={{ fontWeight: 500, borderBottom: "2px solid #f0c040" }}>Building Use</th>
                            <th className="text-muted text-end" style={{ fontWeight: 500, borderBottom: "2px solid #f0c040" }}># Buildings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.buildings?.breakdown || []).map((item) => {
                            const pct = data.buildings?.total > 0 ? Math.round((item.count / data.buildings.total) * 100) : 0;
                            const color = BUILDING_COLORS[item.category_id] || '#666';
                            return (
                              <tr key={item.category_id}>
                                <td>
                                  <span style={{
                                    display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 6,
                                    backgroundColor: color,
                                  }}></span>
                                  {item.category_name}
                                  <div className="progress mt-1" style={{ height: 4 }}>
                                    <div className="progress-bar" style={{ width: `${pct}%`, backgroundColor: color }}></div>
                                  </div>
                                </td>
                                <td className="text-end">{number.fNumber(item.count)} <small className="text-muted">({pct}%)</small></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      <hr />
                      <div className="d-flex justify-content-between">
                        <span className="fw-semibold">Buildings Registered</span>
                        <span className="fw-bold">{number.fNumber(data.buildings?.registered || 0)}</span>
                      </div>

                      {buildingGeoJson?.features && (
                        <div className="d-flex justify-content-between mt-2">
                          <span className="fw-semibold text-muted">Buildings on Map</span>
                          <span className="badge bg-soft-primary text-primary">{buildingGeoJson.features.length.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Population Section */}
                  <div className="card card-animate">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="avatar-sm">
                            <span className="avatar-title bg-soft-primary rounded-circle fs-2">
                              <i className="las la-users text-primary"></i>
                            </span>
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <p className="text-muted mb-1">Population</p>
                          <h3 className="mb-0 fw-bold">{number.fNumber(data.population?.total || 0)}</h3>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className="badge bg-soft-info text-info fs-6">
                          <i className="las la-home me-1"></i>
                          {number.fNumber(data.population?.households || 0)} Households
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Infrastructure Section */}
                  <div className="card card-animate">
                    <div className="card-header">
                      <h6 className="card-title mb-0 text-muted">Infrastructure Access</h6>
                    </div>
                    <div className="card-body">
                      <div className="row text-center">
                        <div className="col-4">
                          <div className="p-2">
                            <i className="las la-bolt fs-2 text-warning"></i>
                            <h4 className="mb-1">{data.infrastructure?.electricity_pct || 0}%</h4>
                            <p className="text-muted mb-0 small">Electricity Access</p>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="p-2">
                            <i className="las la-toilet fs-2 text-info"></i>
                            <h4 className="mb-1">{data.infrastructure?.sewer_pct || 0}%</h4>
                            <p className="text-muted mb-0 small">Sewer Line</p>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="p-2">
                            <i className="las la-tint fs-2 text-primary"></i>
                            <h4 className="mb-1">{data.infrastructure?.water_pct || 0}%</h4>
                            <p className="text-muted mb-0 small">Water Access</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Map */}
                <div className="col-xxl-7 col-lg-7">
                  <div className="card" style={{ height: "calc(100vh - 200px)" }}>
                    <div className="card-body p-1" style={{ position: 'relative' }}>
                      <MapContainer
                        style={{ height: "100%", width: "100%", borderRadius: "3px" }}
                        ref={mapRef}
                        whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
                        scrollWheelZoom
                        center={[-5.8, 39.5]}
                        zoom={9}
                        minZoom={5}
                        maxZoom={18}
                        zoomControl={true}
                      >
                        <LayersControl>
                          {TILE_LAYERS.map((tile) => (
                            <BaseLayer key={tile.name} name={tile.name} checked={tile.checked}>
                              <TileLayer url={tile.url} />
                            </BaseLayer>
                          ))}
                        </LayersControl>

                        {/* Building layer with category colors */}
                        {buildingGeoJson && buildingGeoJson.features && (
                          <FeatureGroup>
                            <GeoJSON
                              key={'buildings-' + (buildingGeoJson.features?.length || 0)}
                              data={buildingGeoJson}
                              style={buildingStyle}
                              onEachFeature={onBuildingFeature}
                            />
                          </FeatureGroup>
                        )}

                        {/* Boundary polygon with rich popup */}
                        {boundaryGeoJson && (
                          <FeatureGroup>
                            <GeoJSON
                              key={JSON.stringify(boundaryGeoJson)}
                              data={boundaryGeoJson}
                              style={() => boundaryPolygonStyle}
                              onEachFeature={onBoundaryFeature}
                            />
                          </FeatureGroup>
                        )}
                      </MapContainer>

                      {/* Building legend overlay */}
                      {buildingGeoJson?.features?.length > 0 && (
                        <div style={{
                          position: 'absolute', bottom: 10, left: 10, zIndex: 1000,
                          background: 'rgba(255,255,255,0.92)', borderRadius: 6,
                          padding: '8px 12px', fontSize: 11, boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                          pointerEvents: 'none',
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>Buildings</div>
                          {Object.entries(BUILDING_COLORS).map(([catId, color]) => (
                            <div key={catId} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                              <div style={{ width: 10, height: 10, backgroundColor: color, marginRight: 6, borderRadius: 2 }}></div>
                              <span>{CATEGORY_MAP[catId]}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!loading && !data && isAdmin && (
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <i className="las la-chart-bar" style={{ fontSize: "48px", color: "#878a99" }}></i>
                      <h5 className="mt-3 text-muted">Select a boundary and click "Load Report" to view the settlement overview</h5>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};
